/**
 * Copyright (c) Microsoft. All rights reserved.
 */
import { Spinner } from '@fluentui/react';
import { DefaultButton, PrimaryButton } from '@fluentui/react/lib/Button';
import { ChoiceGroup, IChoiceGroupOption } from '@fluentui/react/lib/ChoiceGroup';
import { Link } from '@fluentui/react/lib/Link';
import { IPanel, IPanelProps, Panel, PanelType } from '@fluentui/react/lib/Panel';
import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { BasicModal } from 'src/core/components/BasicModal';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisDropdown } from 'src/core/components/LuisDropdown';
import { LuisText } from 'src/core/components/LuisText';
import { LuisTextField } from 'src/core/components/LuisTextField';
import { useFormState } from 'src/core/hooks/useFormState';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { logButtonClick } from 'src/core/services/telemetry/eventsNamesTemplate';
import { getStylist } from 'src/core/themes/luisStylist';
import { FormValidators } from 'src/core/utils/formValidators';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import { ChangeTrainingConfigVersionModal } from 'src/modules/modelTraining/components/ChangeTrainingConfigVersionModal';
import { ControlDatasetOptions } from 'src/modules/modelTraining/components/ControlDatasetOptions';
import { TrainingMode } from 'src/modules/modelTraining/types/trainingSwaggers';
import { DataSplittingModes, TrainingConfigVersion } from 'src/modules/modelTraining/types/trainingTypes';

type TrainingModelType = 'existingModel' | 'newModel';

export type TrainingModelDto = {
    modelName: string;
    labelOrigin?: string;
    trainingMode?: TrainingMode;
    trainingConfigVersion: string;
    datasetSplitMode: DataSplittingModes;
    testingSplitPercentage: number | null;
    trainingSplitPercentage: number | null;
};

export type SubmitTrainingJobPanelProps = IPanelProps & {
    isOpen: boolean;
    tagDataLink: string;
    learnMoreLink: string;
    trainedModelNames: string[];
    currentTrainingModelName: string;
    showTrainingModeChoices?: boolean;
    advancedTrainingPricingLink?: string;
    userDefinedTestingPercentage?: number;
    isUserDefinedSplittingEnabled: boolean;
    userDefinedTrainingPercentage?: number;
    userDefinedOptionDisabledTooltip: string;
    onSubmitJob?: (data: TrainingModelDto) => void;
    trainingConfigVersions: TrainingConfigVersion[];
};

const messages = defineMessages({
    cancel: { id: 'modelTraining.submitTrainingJobPanel.cancel', defaultMessage: 'Cancel' },
    trainModel: { id: 'modelTraining.submitTrainingJobPanel.trainModel', defaultMessage: 'Train' },
    panelClose: { id: 'modelTraining.submitTrainingJobPanel.panelClose', defaultMessage: 'Close' },
    dataSplit: { id: 'modelTraining.submitTrainingJobPanel.dataSplit', defaultMessage: 'Data splitting' },
    selectModel: { id: 'modelTraining.submitTrainingJobPanel.selectModel', defaultMessage: 'Select Model' },
    chooseLabelOrigin: { id: 'modelTraining.submitTrainingJobPanel.chooseLabelOrigin', defaultMessage: 'Choose label origin' },
    chooseLabelOriginDesc: {
        id: 'modelTraining.submitTrainingJobPanel.chooseLabelOriginDesc',
        defaultMessage:
            'Import your labels from Azure Machine Learning or use the current labels in Language Studio. The labels in Azure Machine Learning are more likely to be up to date, but it may take longer to import them. Alternately, you can switch labeling activity to Language Studio from Azure Machine Learning before you start a training job.'
    },
    importLabelsFromAml: {
        id: 'modelTraining.submitTrainingJobPanel.importLabelsFromAml',
        defaultMessage: 'Import latest labels from Azure Machine Learning\u00A0\u00A0\u00A0\u00A0'
    },
    useLsLabels: {
        id: 'modelTraining.submitTrainingJobPanel.useLsLabels',
        defaultMessage: 'Use Language Studio labels (may not be current)'
    },
    trainingMode: { id: 'modelTraining.submitTrainingJobPanel.trainingMode', defaultMessage: 'Training mode' },
    modalTitle: { id: 'modelTraining.submitTrainingJobPanel.modalTitle', defaultMessage: 'Start a training job' },
    panelTitle: { id: 'modelTraining.submitTrainingJobPanel.panelTitle', defaultMessage: 'Start a training job' },
    trainNewModel: { id: 'modelTraining.submitTrainingJobPanel.trainNewModel', defaultMessage: 'Train a new model' },
    enterModelName: { id: 'modelTraining.submitTrainingJobPanel.enterModelName', defaultMessage: 'Enter model name' },
    modalSubmitBtn: { id: 'modelTraining.submitTrainingJobPanel.modalSubmitBtn', defaultMessage: 'Overwrite and train' },
    selectModelPlaceholder: { id: 'modelTraining.submitTrainingJobPanel.selectModelPlaceholder', defaultMessage: 'Select model' },
    advancedTrainingTitle: { id: 'modelTraining.submitTrainingJobPanel.advancedTrainingTitle', defaultMessage: 'Advanced training' },
    standardTrainingTitle: { id: 'modelTraining.submitTrainingJobPanel.standardTrainingTitle', defaultMessage: 'Standard training (free)' },
    advancedTrainingViewPricing: {
        id: 'modelTraining.submitTrainingJobPanel.advancedTrainingViewPricing',
        defaultMessage: 'View pricing.'
    },
    dataSplitLearnMore: {
        id: 'modelTraining.submitTrainingJobPanel.dataSplitLearnMore',
        defaultMessage: 'Learn more about data splitting.'
    },
    overwriteExistingModel: {
        id: 'modelTraining.submitTrainingJobPanel.overwriteExistingModel',
        defaultMessage: 'Overwrite an existing model'
    },
    duplicatedNameErrorMessage: {
        id: 'modelTraining.submitTrainingJobPanel.duplicatedNameErrorMessage',
        defaultMessage: 'This name already exists.'
    },
    invalidModelName: {
        id: 'modelTraining.submitTrainingJobPanel.invalidModelName',
        defaultMessage: 'Model name should include only "-", "_", "." and alphanumeric characters.'
    },
    modalBody: {
        id: 'modelTraining.submitTrainingJobPanel.modalBody',
        defaultMessage: `Your current model with this name will be overwritten.\
To train as a new model, cancel this training job and give the model a new name.`
    },
    controlledDataSplitDesc: {
        id: 'modelTraining.submitTrainingJobPanel.controlledDataSplitDesc',
        defaultMessage: 'We use separate datasets to train your model and test its accuracy.'
    },
    selectModelDesc: {
        id: 'modelTraining.submitTrainingJobPanel.selectModelDesc',
        defaultMessage: `You can train a new model or overwrite an existing one. Training a new model is best at the beginning or for comparing between model performances. Overwriting a model will replace the old model with the new data.`
    },
    selectedTrainingConfigVersion: {
        id: 'modelTraining.submitTrainingJobPanel.selectedTrainingConfigVersion',
        defaultMessage: 'You are currently using the {configVersion} training configuration version.'
    },
    selectedLatestTrainingConfigVersion: {
        id: 'modelTraining.submitTrainingJobPanel.selectedLatestTrainingConfigVersion',
        defaultMessage: 'You are currently using the latest {configVersion} training configuration version.'
    },
    changeTrainingConfigVersion: {
        id: 'modelTraining.submitTrainingJobPanel.changeTrainingConfigVersion',
        defaultMessage: 'Click here to change.'
    },
    standardTrainingDesc: {
        id: 'modelTraining.submitTrainingJobPanel.standardTrainingDesc',
        defaultMessage: 'Faster training times for quicker iterations. Only available in English. Free of charge.'
    },
    advancedTrainingDesc: {
        id: 'modelTraining.submitTrainingJobPanel.advancedTrainingDesc',
        defaultMessage: 'Trains using fine-tuned neural network transformer models. Can train multilingual projects.'
    },
    splittingWithAml: {
        id: 'modelTraining.submitTrainingJobPanel.splittingWithAml',
        defaultMessage: 'Currently unavailable. To use data splitting, disconnect your project from Azure Machine Learning.'
    },
    importingLabels: {
        id: 'modelTraining.submitTrainingJobPanel.importingLabels',
        defaultMessage: 'Importing labels from Azure Machine Learning'
    },
    importingLabelsCompleted: {
        id: 'modelTraining.submitTrainingJobPanel.importingLabelsCompleted',
        defaultMessage: 'Importing labels from Azure Machine Learning is Completed'
    },
    importingLabelsFailed: {
        id: 'modelTraining.submitTrainingJobPanel.importingLabelsFailed',
        defaultMessage: 'Importing labels from Azure Machine Learning is Failed'
    }
});

const { styleComponent } = getStylist('SubmitTrainingJobPanel');
const Footer = styleComponent<IStackProps>(Stack)('Footer', theme => ({ height: 64, borderTop: `1px solid ${theme.footerBorderColor}` }));
const horizontalChoiceGroupStyles = { flexContainer: { display: 'flex' } };

export const SubmitTrainingJobPanel = observer((props: SubmitTrainingJobPanelProps) => {
    const {
        onDismiss,
        layerProps,
        onSubmitJob,
        tagDataLink,
        learnMoreLink,
        isOpen = false,
        trainedModelNames,
        trainingConfigVersions,
        showTrainingModeChoices,
        currentTrainingModelName,
        advancedTrainingPricingLink,
        userDefinedTestingPercentage,
        userDefinedTrainingPercentage,
        isUserDefinedSplittingEnabled,
        userDefinedOptionDisabledTooltip
    } = props;
    const { localize } = useLocalization();
    const { serviceName } = getPortalFromUrl();
    const { rootStore, dispatcher } = useGlobalState();
    const isAmlConnected = !!rootStore.currentCustomTextProjectStore?.amlProjectPath;
    const isLabelingLocked = !!rootStore.currentCustomTextProjectStore?.isLabelingLocked;

    const sortedTrainingConfigVersions = trainingConfigVersions
        .sort((a, b) => b.modelExpirationDate.getTime() - a.modelExpirationDate.getTime());

    // Check if we can find a config version that has isLatest = true. If not, then fallback to the version with the latest expiration date.
    const latestTrainingConfigVersion = trainingConfigVersions.find((configVersion) => configVersion.isLatest === true) || sortedTrainingConfigVersions[0];
    const availableTrainingConfigVersions = sortedTrainingConfigVersions.map(
        r => ({
            modelExpirationDate: r.modelExpirationDate,
            trainingConfigVersion: r.trainingConfigVersion,
            isLatest: r.isLatest || r.trainingConfigVersion === latestTrainingConfigVersion.trainingConfigVersion
        } as TrainingConfigVersion)
    );

    const defaultTrainingConfigVersion = latestTrainingConfigVersion.trainingConfigVersion;
    const { formState, formStateDto, updateFormState } = useFormState<TrainingModelDto>({
        trainingMode: ['standard'],
        datasetSplitMode: [isLabelingLocked ? 'percentage' : isUserDefinedSplittingEnabled ? 'manual' : 'percentage'],
        testingSplitPercentage: [
            20,
            FormValidators.createRequiredValidator(),
            FormValidators.createNumberRangeValidator({ range: [0, 100] })
        ],
        trainingSplitPercentage: [
            80,
            FormValidators.createRequiredValidator(),
            FormValidators.createNumberRangeValidator({ range: [0, 100] })
        ],
        modelName: [
            '',
            FormValidators.createRequiredValidator(),
            FormValidators.createBlacklistValidator({
                mode: 'include',
                errorMessage: messages.invalidModelName,
                characterList: LuisConstants.ResourceLimits.AllowedNameReges
            })
        ],
        trainingConfigVersion: [defaultTrainingConfigVersion, FormValidators.createRequiredValidator()],
        labelOrigin: [isLabelingLocked ? 'aml' : null]
    });

    const panelRef = React.useRef<IPanel>();
    const [modelType, setModelType] = React.useState<TrainingModelType>('newModel');

    const arePercentagesValid = React.useMemo(
        () =>
            formStateDto.trainingSplitPercentage > 0 &&
            formStateDto.testingSplitPercentage >= 0 &&
            formStateDto.testingSplitPercentage < 100 &&
            formStateDto.trainingSplitPercentage <= 100,
        [formStateDto.trainingSplitPercentage, formStateDto.testingSplitPercentage]
    );

    const isDuplicateName = modelType === 'newModel' && trainedModelNames.some(name => name === formStateDto.modelName);
    const isTrainButtonDisabled = React.useMemo(() => !arePercentagesValid || !formState.form.valid || isDuplicateName, [
        isDuplicateName,
        arePercentagesValid,
        formState.form.valid
    ]);
    const [overrideModalHidden, setOverrideModalHidden] = React.useState(true);
    const [trainingConfigVersionModalHidden, setTrainingConfigVersionModalHidden] = React.useState(true);

    const projectIdForTelemetry = rootStore.currentCluProjectStore?.id ?? rootStore.currentCustomTextProjectStore?.id;

    const onModelTypeChange = (opt: IChoiceGroupOption) => {
        setModelType(opt.key as TrainingModelType);
        updateFormState({ modelName: '' });
    };

    const onImportingTypeChange = (opt: IChoiceGroupOption) => {
        updateFormState({ labelOrigin: opt.key });
    };

    const onTrainingModeChange = (opt: IChoiceGroupOption) => {
        updateFormState({ trainingMode: opt.key as TrainingMode });
    };

    const onChangeTrainingPercentage = (newValue: string) => {
        if (!isNaN(parseInt(newValue, 10))) {
            const newTrainingPercentage = parseInt(newValue, 10);
            updateFormState({ trainingSplitPercentage: newTrainingPercentage, testingSplitPercentage: 100 - newTrainingPercentage });
        }
        if (newValue === '') {
            updateFormState({ trainingSplitPercentage: null, testingSplitPercentage: null });
        }
    };

    const onChangeTestingPercentage = (newValue: string) => {
        if (!isNaN(parseInt(newValue, 10))) {
            const newTestingPercentage = parseInt(newValue, 10);
            updateFormState({ trainingSplitPercentage: 100 - newTestingPercentage, testingSplitPercentage: newTestingPercentage });
        }
        if (newValue === '') {
            updateFormState({ trainingSplitPercentage: null, testingSplitPercentage: null });
        }
    };

    const onChangeDataSplittingOption = (newOption: DataSplittingModes) => {
        if (newOption === 'manual') {
            updateFormState({ datasetSplitMode: newOption, trainingSplitPercentage: 80, testingSplitPercentage: 20 });
        } else {
            updateFormState({ datasetSplitMode: newOption });
        }
    };

    const onTrain = () => {
        if (isTrainButtonDisabled) {
            return;
        }

        logButtonClick(serviceName, 'Training', 'SubmitTraining', null, projectIdForTelemetry);

        if (modelType === 'existingModel') {
            setOverrideModalHidden(false);
        } else {
            onSubmitJob(formStateDto);
        }
    };

    const onCancel = () => {
        logButtonClick(serviceName, 'Training', 'CancelTraining', null, projectIdForTelemetry);
        panelRef.current.dismiss();
    };

    const onChangeTrainingConfigVersion = ({ trainingConfigVersion: selectedVersion }: { trainingConfigVersion: string }) => {
        updateFormState({ trainingConfigVersion: selectedVersion });
        setTrainingConfigVersionModalHidden(true);
    };

    const importLabelTracker = useProgressTracker({
        persistOnComplete: true
    });

    const onSubmit = () => {
        if (formState.fields.labelOrigin.value === 'aml') {
            dispatcher.dispatch('importAmlLabels', {
                tracker: importLabelTracker,
                onTrain,
                toastData: {
                    running: { title: localize(messages.importingLabels) },
                    completed: { title: localize(messages.importingLabelsCompleted) },
                    failed: { title: localize(messages.importingLabelsFailed) }
                }
            });
        } else {
            onTrain();
        }
    };

    const onRenderFooterContent = () => (
        <Footer horizontal tokens={{ padding: 15 }} verticalAlign="center">
            <Stack horizontal reversed tokens={{ childrenGap: 8 }}>
                {importLabelTracker?.isRunning ? (
                    <Stack horizontal tokens={{ childrenGap: 8 }}>
                        <Spinner />
                        <Stack.Item align="center">
                            <LuisText>{localize(messages.importingLabels)}</LuisText>
                        </Stack.Item>
                    </Stack>
                ) : null}
                <DefaultButton disabled={importLabelTracker.isRunning} onClick={onCancel}>
                    {localize(messages.cancel)}
                </DefaultButton>
                <PrimaryButton
                    data-automation-id="trainModelButton"
                    disabled={importLabelTracker.isRunning || isTrainButtonDisabled}
                    onClick={onSubmit}
                    onSubmit={onSubmit}>
                    {localize(messages.trainModel)}
                </PrimaryButton>
            </Stack>
        </Footer>
    );

    const labelingOptions: IChoiceGroupOption[] = [
        { key: 'aml', text: messages.importLabelsFromAml.defaultMessage },
        { key: 'ls', text: localize(messages.useLsLabels) }
    ];

    const modelNameOptions: IChoiceGroupOption[] = [
        {
            key: 'newModel',
            text: localize(messages.trainNewModel),
            onRenderField: (fieldProps, defaultRenderer) => (
                <Stack tokens={{ childrenGap: 8 }}>
                    {defaultRenderer(fieldProps)}
                    {modelType === 'newModel' && (
                        <LuisTextField
                            ariaLabel={localize(messages.trainNewModel)}
                            aria-required
                            styles={{ root: { width: 240 } }}
                            onEnterKeyUp={onTrain}
                            placeholder={localize(messages.enterModelName)}
                            data-automation-id="modelName"
                            onChange={(_, newValue) => updateFormState({ modelName: newValue })}
                            errorMessage={
                                formState.fields.modelName.errorMessage ??
                                (isDuplicateName && localize(messages.duplicatedNameErrorMessage))
                            }
                        />
                    )}
                </Stack>
            )
        },
        {
            key: 'existingModel',
            text: localize(messages.overwriteExistingModel),
            onRenderField: (fieldProps, defaultRenderer) => (
                <Stack tokens={{ childrenGap: 10 }}>
                    {defaultRenderer(fieldProps)}
                    {modelType !== 'newModel' && (
                        <LuisDropdown
                            aria-required
                            styles={{ root: { width: 240 } }}
                            selectedKey={currentTrainingModelName}
                            errorMessage={formState.fields.modelName.errorMessage}
                            placeholder={localize(messages.selectModelPlaceholder)}
                            onChange={(_, option) => updateFormState({ modelName: option.text })}
                            options={trainedModelNames.map(model => ({ key: model, text: model }))}
                        />
                    )}
                </Stack>
            )
        }
    ];

    /**
     * There is a fluent ui issue https://github.com/microsoft/fluentui/issues/21252.
     * Adding z-index to fields is a workaround till fixing this bug.
     */
    const trainingModeOptions: IChoiceGroupOption[] = [
        {
            key: 'standard',
            text: localize(messages.standardTrainingTitle),
            onRenderField: (fieldProps, defaultRenderer) => (
                <Stack tokens={{ childrenGap: 8 }}>
                    {defaultRenderer(fieldProps)}
                    <LuisText styles={{ root: { zIndex: 1 } }}>{localize(messages.standardTrainingDesc)}</LuisText>
                </Stack>
            )
        },
        {
            key: 'advanced',
            text: localize(messages.advancedTrainingTitle),
            onRenderField: (fieldProps, defaultRenderer) => (
                <Stack tokens={{ childrenGap: 8 }}>
                    {defaultRenderer(fieldProps)}
                    <LuisText styles={{ root: { zIndex: 1 } }}>
                        {localize(messages.advancedTrainingDesc)}
                        &nbsp;
                        <ExternalLink href={advancedTrainingPricingLink}>{localize(messages.advancedTrainingViewPricing)}</ExternalLink>
                    </LuisText>
                </Stack>
            )
        }
    ];

    const submitOverwriteJob = () => {
        setOverrideModalHidden(true);
        onSubmitJob(formStateDto);
    };

    return (
        <>
            <Panel
                componentRef={panelRef}
                isOpen={isOpen}
                onDismiss={!importLabelTracker.isRunning && onDismiss}
                data-automation-id="submitTrainingJobPanel"
                headerText={localize(messages.panelTitle)}
                closeButtonAriaLabel={localize(messages.panelClose)}
                onRenderFooterContent={onRenderFooterContent}
                type={PanelType.smallFluid}
                isFooterAtBottom
                onOuterClick={() => { }} // Prevents outer clicks from dismissing panel
                styles={{ contentInner: { flexDirection: 'column ' } }}
                layerProps={layerProps}>
                {isLabelingLocked ? (
                    <Stack tokens={{ childrenGap: 20 }}>
                        <Stack tokens={{ childrenGap: 5 }}>
                            <Stack.Item tokens={{ padding: '20px 0 0 0' }}>
                                <LuisText fontWeight={600} size="16px">
                                    {localize(messages.chooseLabelOrigin)}
                                </LuisText>
                            </Stack.Item>
                            <LuisText>{localize(messages.chooseLabelOriginDesc)}</LuisText>

                            <Stack horizontal>
                                <ChoiceGroup
                                    selectedKey={formStateDto.labelOrigin}
                                    options={labelingOptions}
                                    styles={horizontalChoiceGroupStyles}
                                    onChange={(_, opt) => onImportingTypeChange(opt)}
                                />
                            </Stack>
                        </Stack>
                    </Stack>
                ) : null}

                <Stack tokens={{ childrenGap: 20 }}>
                    <Stack tokens={{ childrenGap: 5 }}>
                        <Stack.Item tokens={{ padding: '20px 0 0 0' }}>
                            <LuisText as="h3" fontWeight={600} size="16px">
                                {localize(messages.selectModel)}
                            </LuisText>
                        </Stack.Item>
                        <LuisText>{localize(messages.selectModelDesc)}</LuisText>

                        <Stack.Item tokens={{ padding: '15px 0 15px 0' }}>
                            <LuisText size="12px">
                                {localize(
                                    formStateDto.trainingConfigVersion === defaultTrainingConfigVersion
                                        ? messages.selectedLatestTrainingConfigVersion
                                        : messages.selectedTrainingConfigVersion,
                                    { configVersion: formStateDto.trainingConfigVersion }
                                )}
                                &nbsp;
                                <Link onClick={() => setTrainingConfigVersionModalHidden(false)}>
                                    {localize(messages.changeTrainingConfigVersion)}
                                </Link>
                            </LuisText>
                        </Stack.Item>

                        <Stack.Item>
                            <ChoiceGroup options={modelNameOptions} selectedKey={modelType} onChange={(_, opt) => onModelTypeChange(opt)} />
                        </Stack.Item>
                    </Stack>

                    {showTrainingModeChoices && (
                        <Stack tokens={{ childrenGap: 5 }}>
                            <Stack.Item tokens={{ padding: '20px 0 0 0' }}>
                                <LuisText fontWeight={600} size="16px">
                                    {localize(messages.trainingMode)}
                                </LuisText>
                            </Stack.Item>

                            <Stack.Item>
                                <ChoiceGroup
                                    options={trainingModeOptions}
                                    selectedKey={formStateDto.trainingMode}
                                    onChange={(_, opt) => onTrainingModeChange(opt)}
                                />
                            </Stack.Item>
                        </Stack>
                    )}

                    <Stack tokens={{ childrenGap: 5 }}>
                        <Stack.Item tokens={{ padding: '20px 0 0 0' }}>
                            <LuisText as="h3" fontWeight={600} size="16px">
                                {localize(messages.dataSplit)}
                            </LuisText>
                        </Stack.Item>

                        {isAmlConnected ? (
                            <LuisText>
                                {localize(messages.splittingWithAml)}
                                &nbsp;
                                <ExternalLink href={learnMoreLink}>{localize(messages.dataSplitLearnMore)}</ExternalLink>
                            </LuisText>
                        ) : (
                            <LuisText>
                                {localize(messages.controlledDataSplitDesc)}
                                &nbsp;
                                <ExternalLink href={learnMoreLink}>{localize(messages.dataSplitLearnMore)}</ExternalLink>
                            </LuisText>
                        )}

                        <Stack horizontal>
                            <ControlDatasetOptions
                                dismissModal={onDismiss}
                                tagDataLink={tagDataLink}
                                arePercentagesValid={arePercentagesValid}
                                dataSplittingOption={formStateDto.datasetSplitMode}
                                onChangeTestingPercentage={onChangeTestingPercentage}
                                onChangeTrainingPercentage={onChangeTrainingPercentage}
                                testingPercentage={formStateDto.testingSplitPercentage}
                                trainingPercentage={formStateDto.trainingSplitPercentage}
                                onChangeDataSplittingOption={onChangeDataSplittingOption}
                                userDefinedTestingPercentage={userDefinedTestingPercentage}
                                userDefinedTrainingPercentage={userDefinedTrainingPercentage}
                                isUserDefinedSplittingEnabled={isUserDefinedSplittingEnabled}
                                userDefinedOptionDisabledTooltip={userDefinedOptionDisabledTooltip}
                            />
                        </Stack>
                    </Stack>
                </Stack>
            </Panel>

            <BasicModal
                valid
                hidden={overrideModalHidden}
                onSubmit={submitOverwriteJob}
                onDismiss={() => setOverrideModalHidden(true)}
                data-automation-id="confirmModelOverWriteModal"
                doneButtonText={localize(messages.modalSubmitBtn)}
                dialogContentProps={{
                    title: localize(messages.modalTitle),
                    subText: localize(messages.modalBody),
                    styles: { content: { whiteSpace: 'pre-line' } }
                }}
            />
            <ChangeTrainingConfigVersionModal
                valid
                hidden={trainingConfigVersionModalHidden}
                onSubmit={onChangeTrainingConfigVersion}
                onDismiss={() => setTrainingConfigVersionModalHidden(true)}
                data-automation-id="changeTrainingConfigVersionModal"
                initialTrainingConfigVersion={formStateDto.trainingConfigVersion}
                trainingConfigVersions={availableTrainingConfigVersions}
            />
        </>
    );
});
